import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { User, HelpCircle, LogOut } from "lucide-react";

import { UserContext } from "../../context/UserContext";
import { EXHIBITOR } from "../../../constants/constants";
import defaultPic from "./default_avatar.jpg";

const AvatarMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, loading, logout } = useContext(UserContext);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Determine correct image and name to display
  const profileImage = user?.profileImage || user?.profile_image || defaultPic;
  const displayName =
    user?.role === EXHIBITOR
      ? user?.companyName?.value
      : `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  const displayRole = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : "User";

  return (
    <Box>
      <Tooltip title="Account settings">
        <IconButton onClick={handleClick} sx={{ p: 0 }}>
          <Avatar
            sx={{
              height: 42,
              width: 42,
              border: "2px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              transition: "border-color 0.2s ease-in-out",
              "&:hover": { borderColor: "#3b82f6" },
            }}
            alt={displayName}
            src={profileImage}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: 260,
              overflow: "visible",
              mt: 1.5,
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 18,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
                borderTop: "1px solid #e2e8f0",
                borderLeft: "1px solid #e2e8f0",
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* Beautiful Header Profile Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 2,
            px: 2,
            bgcolor: "#f8fafc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Avatar
            src={profileImage}
            alt={displayName}
            sx={{
              width: 72,
              height: 72,
              mb: 1.5,
              border: "3px solid white",
              boxShadow: "0px 4px 10px rgba(0,0,0,0.08)",
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            {displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            {displayRole}
          </Typography>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Menu Items with modern hover styles */}
        <Box sx={{ px: 1, pb: 1 }}>
          <MenuItem
            component={Link}
            to={`/${user?.role}/profile`}
            sx={{
              borderRadius: "8px",
              mb: 0.5,
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            <ListItemIcon>
              <User size={18} color="#475569" />
            </ListItemIcon>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#334155" }}
            >
              Profile
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={() => navigate(`/${user?.role}/support-request`)}
            sx={{
              borderRadius: "8px",
              mb: 0.5,
              "&:hover": { bgcolor: "#f1f5f9" },
            }}
          >
            <ListItemIcon>
              <HelpCircle size={18} color="#475569" />
            </ListItemIcon>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#334155" }}
            >
              Support Requests
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              logout();
            }}
            sx={{ borderRadius: "8px", "&:hover": { bgcolor: "#fee2e2" } }}
          >
            <ListItemIcon>
              <LogOut size={18} color="#ef4444" />
            </ListItemIcon>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#ef4444" }}
            >
              Logout
            </Typography>
          </MenuItem>
        </Box>
      </Menu>
    </Box>
  );
};

export default AvatarMenu;
